import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * MobileAppBar — sticky top chrome for mobile pages.
 *
 * Slots: leading (menu/back), title, trailing (one or two icon actions).
 * Heights & safe-area-top padding are managed by the bar; pages just render it
 * inside their layout and the bar handles its own positioning. Hidden at md+
 * because desktop has the sidebar.
 *
 * The bar is monochrome by design (black/white tokens only) to stay consistent
 * with the rest of the design language.
 */
export type MobileAppBarProps = {
  leading?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  /**
   * If true, the bar is rendered without the bottom border. Useful when the
   * page content has its own header-like band right below the bar.
   */
  borderless?: boolean;
  className?: string;
};

export function MobileAppBar({
  leading,
  title,
  subtitle,
  trailing,
  borderless = false,
  className,
}: MobileAppBarProps): React.ReactElement {
  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex items-center gap-2 bg-background pt-safe md:hidden",
        borderless ? "" : "border-b",
        className,
      )}
    >
      <div className="flex h-app-bar w-full items-center gap-2 px-2">
        <div className="flex min-w-touch items-center">
          {leading}
        </div>
        <div className="min-w-0 flex-1">
          {title && (
            <div className="truncate text-sm font-semibold tracking-tight">
              {title}
            </div>
          )}
          {subtitle && (
            <div className="truncate text-[11px] text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>
        <div className="flex min-w-touch items-center justify-end gap-1">
          {trailing}
        </div>
      </div>
    </header>
  );
}
